import type { FC } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTrash as TrashIcon } from 'react-icons/fa';
import { generatePath } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
} from '@mui/material';
import type { AxiosError } from 'axios';

import { SEARCH_FORM_SETTINGS } from 'src/constants/settings';
import type { Report } from 'src/types/report';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import ListSearch from 'src/components/common/ListWithSearch/components/ListSearch';
import Modal from 'src/components/common/Modal';
import ReportShareModal from 'src/components/common/ReportShareModal';
import type { ReportsStatusProps } from 'src/components/report/OrgUnitReportsPage/ReportStatusContext';
import useReportConfigMaps from 'src/components/reportConfig/hooks/useReportConfigMaps';

import AdvancedSearch from './components/AdvancedSearch';
import ReportTableHead from './components/ReportTableHead';
import ReportTableRow from './components/ReportTableRow';

import { styles } from './index.styles';

export const REPORT_TABLE_TEST_WRAPPER_ID = 'report-table-wrapper';
export const REPORT_TABLE_TEST_ID = 'report-table';
export const REPORT_TABLE_SEARCH_TEST_ID = 'report-table-search';
export const REPORT_TABLE_SPINNER_TEST_ID = 'report-table-spinner';
export const REPORT_TABLE_MODAL_DELETE_BUTTON_TEST_ID =
  'report-table-modal-delete-button';

interface Pagination {
  count: number;
  hasNextPage: boolean;
  offset: number;
}

interface ReportTableProps {
  advancedSearchFilters?: {
    report_type: string;
    file_type: string;
    date_start: string;
    date_end: string;
  };
  fetchNextPage?: () => Promise<void>;
  handleAdvancedSearchFilterChange?: (key: any, value: any) => void;
  handleSearch?: (value: string | null, searchParameter?: string) => void;
  loading: boolean;
  pagination?: Pagination;
  refetchReports?: () => void;
  reports: Report[];
  reportsStatus?: ReportsStatusProps;
  showCustomReports?: boolean;
}

const REPORT_TABLE_SIZE = 10;

export const ReportTable: FC<ReportTableProps> = ({
  advancedSearchFilters,
  fetchNextPage,
  handleAdvancedSearchFilterChange,
  handleSearch,
  loading,
  pagination,
  refetchReports,
  reports,
  reportsStatus,
  showCustomReports,
}) => {
  const { t } = useTranslation('report');
  const [showShareModal, setShowShareModal] = useState(false);
  const [reportToShare, setReportToShare] = useState<Report>();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFetchingNextReports, setIsFetchingNextReports] = useState(false);
  const {
    reportNamesPerReportType,
    abbreviationPerFileType,
    reportConfigsLoading,
  } = useReportConfigMaps();

  const loadNextPage = () => {
    if (isFetchingNextReports || !fetchNextPage) return;

    setIsFetchingNextReports(true);
    fetchNextPage().finally(() => setIsFetchingNextReports(false));
  };

  const openModalAndGetReportObject = (reportId: number) => {
    setSelectedReport(reports.find((report) => report.id === reportId));
    setModalIsOpen(true);
  };

  const openShareModal = (reportId: number) => {
    setReportToShare(reports.find((report) => report.id === reportId));
    setShowShareModal(true);
  };

  const closeShareModal = (didShare = false) => {
    setReportToShare(undefined);
    setShowShareModal(false);

    // if we shared a report, we should update the list to reflect the changes
    if (didShare && refetchReports) {
      refetchReports();
    }
  };

  const closeModalAndResetReport = () => {
    setErrorMessage(null);
    setModalIsOpen(false);
    setSelectedReport(undefined);
  };

  const deleteReport = () => {
    if (!selectedReport) return;
    const requestEndpoint = generatePath(apiPaths.REPORTS.DETAIL, {
      id: selectedReport.id,
    });

    return HttpService.delete(requestEndpoint, {})
      .then(() => {
        closeModalAndResetReport();
        refetchReports && refetchReports();
      })
      .catch((error: AxiosError<{ detail: string }>) => {
        const message = error.response
          ? `"${error.response.data?.detail}" (Status: ${error.response.status} ${error.response?.statusText})`
          : 'Unknown Error';
        setErrorMessage(t('reportInfo.deleteErrorMessage', { message }));
      });
  };

  return (
    <Box sx={styles.reportTable} data-test-id={REPORT_TABLE_TEST_WRAPPER_ID}>
      {!!showCustomReports && (
        <Stack
          direction="column"
          spacing={1}
          sx={styles.search}
          data-test-id={REPORT_TABLE_SEARCH_TEST_ID}
        >
          {!!handleSearch && (
            <Box
              sx={{
                width: '25rem',
              }}
            >
              <ListSearch
                searchInputPlaceholder={t('sidebar.searchReport')}
                handleSearch={handleSearch}
              />
            </Box>
          )}

          {!!(handleAdvancedSearchFilterChange && advancedSearchFilters) && (
            <AdvancedSearch
              advancedSearchFilters={advancedSearchFilters}
              handleAdvancedSearchFilterChange={
                handleAdvancedSearchFilterChange
              }
            />
          )}
        </Stack>
      )}
      {loading ? (
        <Stack
          direction="row"
          sx={{ justifyContent: 'center', ...styles.spinner }}
        >
          <CircularProgress data-test-id={REPORT_TABLE_SPINNER_TEST_ID} />
        </Stack>
      ) : (
        <Fragment>
          {(!reports || reports.length === 0) && showCustomReports ? (
            <Stack
              direction="row"
              sx={{
                justifyContent: 'center',
              }}
            >
              <Box sx={styles.panel}>{t('noReportsFound')}.</Box>
            </Stack>
          ) : (
            <Fragment>
              <TableContainer>
                <Table size="small" data-test-id={REPORT_TABLE_TEST_ID}>
                  <TableHead>
                    <ReportTableHead showCustomReports={!!showCustomReports} />
                  </TableHead>
                  <TableBody>
                    {reports.map((report, i) => (
                      <ReportTableRow
                        key={`${report.id}-${i}`}
                        onDelete={openModalAndGetReportObject}
                        onShare={openShareModal}
                        showCustomReports={showCustomReports}
                        report={report}
                        reportStatus={reportsStatus && reportsStatus[report.id]}
                        reportNamesPerReportType={reportNamesPerReportType}
                        abbreviationPerFileType={abbreviationPerFileType}
                        loadingReportConfigData={reportConfigsLoading}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Modal
                headline={t('reportDeleteHeadline', {
                  name: selectedReport ? selectedReport.name : '',
                })}
                icon={<TrashIcon size={23} />}
                isOpen={modalIsOpen}
                message={
                  errorMessage ||
                  t('deleteWarning', {
                    itemType: 'report',
                    context: 'report',
                  })
                }
                onClose={closeModalAndResetReport}
              >
                <DialogActions>
                  <Button
                    fullWidth
                    onClick={closeModalAndResetReport}
                    variant="cta"
                    color="secondary"
                  >
                    {errorMessage ? t('common:okay') : t('common:cancel')}
                  </Button>

                  {!errorMessage && (
                    <Button
                      fullWidth
                      onClick={deleteReport}
                      variant="cta"
                      data-test-id={REPORT_TABLE_MODAL_DELETE_BUTTON_TEST_ID}
                    >
                      {t('deleteItem', { itemType: t('report') })}
                    </Button>
                  )}
                </DialogActions>
              </Modal>
            </Fragment>
          )}
        </Fragment>
      )}
      {reports?.length > 0 &&
        showCustomReports &&
        !isFetchingNextReports &&
        !loading &&
        pagination?.hasNextPage && (
          <Box
            sx={{ ...styles.buttonWrapper, pt: 4, justifyContent: 'center' }}
          >
            <Button onClick={loadNextPage} variant="cta">
              {t('loadMore', {
                itemType: t('report', { count: 2 }),
              })}
            </Button>
          </Box>
        )}
      {isFetchingNextReports && (
        <Stack direction="row" sx={{ justifyContent: 'center' }}>
          <CircularProgress />
        </Stack>
      )}
      {showShareModal && reportToShare && (
        <ReportShareModal
          headline={t('share', {
            itemType: t('report'),
            name: reportToShare.name,
          })}
          isReport
          onClose={closeShareModal}
          reportOrDataline={reportToShare}
        />
      )}
    </Box>
  );
};

export interface CustomReportTableProps
  extends Pick<
    ReportTableProps,
    | 'handleAdvancedSearchFilterChange'
    | 'handleSearch'
    | 'reports'
    | 'advancedSearchFilters'
  > {
  searchUrl?: string;
}

export const CustomReportTable: FC<CustomReportTableProps> = ({
  advancedSearchFilters,
  handleAdvancedSearchFilterChange,
  handleSearch,
  reports: externalReports,
  searchUrl = '',
}) => {
  const [reports, setReports] = useState(externalReports);
  const [isLoading, setIsLoading] = useState(false);
  const inputTimeout = useRef<NodeJS.Timeout>();
  const [pagination, setPagination] = useState<Pagination>({
    count: 0,
    hasNextPage: false,
    offset: 0,
  });

  const fetchSearchResults = (url: string, reset = true) => {
    if (!reset && reports.length >= pagination.count) {
      // Early return in case the page has reached its end
      return Promise.resolve();
    }
    return HttpService.get(
      url,
      {
        ...advancedSearchFilters,
        no_draft: true,
        reset,
        offset: reset ? 0 : pagination.offset,
      },
      true,
    )
      .then((response: any) => {
        const {
          data: { results: searchResultItems, count },
        } = response;
        let nextOffset = REPORT_TABLE_SIZE;
        let nextReports = searchResultItems;
        if (!reset) {
          nextOffset = pagination.offset + REPORT_TABLE_SIZE;
          nextReports = [...reports, ...searchResultItems];
        }
        setReports(nextReports);
        setPagination({
          count,
          hasNextPage: count > nextReports.length,
          offset: nextOffset,
        });
        setIsLoading(false);
      })
      .catch(() => {
        setReports([]);
        setIsLoading(false);
      });
  };

  const refetchReports = () => {
    setIsLoading(true);
    const requestEndpoint = searchUrl || apiPaths.GET_CUSTOM_REPORTS;
    return fetchSearchResults(requestEndpoint);
  };

  const fetchNextPage = () => {
    if (!pagination.hasNextPage || isLoading) {
      return Promise.resolve();
    }
    const requestEndpoint = searchUrl || apiPaths.GET_CUSTOM_REPORTS;
    return fetchSearchResults(requestEndpoint, false);
  };

  useEffect(() => {
    // Clear a possible input delay timeout on every input update.
    clearTimeout(inputTimeout.current);
    setIsLoading(true);
    setPagination({
      count: 0,
      hasNextPage: false,
      offset: 0,
    });
    inputTimeout.current = setTimeout(() => {
      const requestEndpoint = searchUrl || apiPaths.GET_CUSTOM_REPORTS;
      fetchSearchResults(requestEndpoint);
    }, SEARCH_FORM_SETTINGS.DEBOUNCE_TIME);
    return () => clearTimeout(inputTimeout.current);
  }, [advancedSearchFilters, searchUrl]);

  return (
    <ReportTable
      advancedSearchFilters={advancedSearchFilters}
      handleAdvancedSearchFilterChange={handleAdvancedSearchFilterChange}
      handleSearch={handleSearch}
      loading={isLoading}
      reports={reports}
      showCustomReports
      pagination={pagination}
      fetchNextPage={fetchNextPage}
      refetchReports={refetchReports}
    />
  );
};

export default ReportTable;
